import React from 'react';
import { Link } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from '@/components/ui/toast';

/**
 * "Needs review" view for Products / Customers that AFM linked to an EXISTING
 * Weightrax record on their first push. Until reviewed, AFM doesn't push them
 * (so it can't overwrite Atlas's Weightrax data).
 *
 * One card per record with AFM's and Weightrax's versions side by side; tick
 * any number, then keep AFM's or Weightrax's details for all of them at once.
 * Each action is explained — and the exact changes listed — before it runs.
 */

interface ReviewSite { weightraxSiteId: number; weightraxId: number | null; linkedExisting: boolean; weightrax: Record<string, string | null> | null }
interface ReviewItem { id: string; afm: Record<string, string | null>; sites: ReviewSite[]; needsReview: boolean; differences: string[] }
interface ResolveResult { id: string; ok: boolean; error?: string }
type Keep = 'afm' | 'weightrax';

const KINDS = {
  product: {
    title: 'Products', noun: 'product', nouns: 'products', wtxNoun: 'material',
    endpoint: '/catalogue-items', listPath: '/operations/catalogue-items',
    fields: [['description', 'Description'], ['unit', 'Unit']] as [string, string][],
    heading: (i: ReviewItem) => `${i.afm.code ?? '—'}${i.afm.itemCode && i.afm.itemCode !== i.afm.code ? ` · ${i.afm.itemCode}` : ''}`,
  },
  customer: {
    title: 'Customers', noun: 'customer', nouns: 'customers', wtxNoun: 'customer',
    endpoint: '/master-data/customers', listPath: '/master-data/customers',
    fields: [['name', 'Name']] as [string, string][],
    heading: (i: ReviewItem) => `${i.afm.code ?? '—'} · ${i.afm.name ?? ''}`,
  },
};
export type WeightraxReviewKind = keyof typeof KINDS;

const show = (v: string | null | undefined) => (v == null || v === '' ? '—' : v);
const linkedSite = (i: ReviewItem) => i.sites.find((s) => s.linkedExisting && s.weightrax) ?? i.sites.find((s) => s.linkedExisting) ?? null;

export function WeightraxReviewPage({ kind }: { kind: WeightraxReviewKind }) {
  const cfg = KINDS[kind];
  const url = `${cfg.endpoint}/weightrax-review`;
  const qc = useQueryClient();
  const { data: items = [], isLoading, error, refetch, isFetching } = useQuery<ReviewItem[]>({
    queryKey: [url],
    queryFn: async () => {
      const r = await api<ReviewItem[]>(url);
      if (!r.success) throw new Error(r.error?.message || 'Could not load the review list');
      return r.data ?? [];
    },
    refetchOnMount: 'always',
  });
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const [pending, setPending] = React.useState<Keep | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [failures, setFailures] = React.useState<Record<string, string>>({});

  // Drop selections for records that have left the list (resolved elsewhere).
  React.useEffect(() => {
    setSelected((prev) => new Set([...prev].filter((id) => items.some((i) => i.id === id))));
  }, [items]);

  const toggle = (id: string) => setSelected((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const chosen = items.filter((i) => selected.has(i.id));
  const allSelected = items.length > 0 && chosen.length === items.length;
  const unreadable = chosen.filter((i) => !linkedSite(i)?.weightrax);

  const run = async (keep: Keep) => {
    setBusy(true);
    const r = await api<ResolveResult[]>(url, { method: 'POST', body: JSON.stringify({ ids: chosen.map((i) => i.id), keep }) });
    setBusy(false);
    setPending(null);
    if (!r.success) { toast('Review failed', r.error?.message, 'error'); return; }
    const results = r.data ?? [];
    const failed = results.filter((x) => !x.ok);
    setFailures(Object.fromEntries(failed.map((f) => [f.id, f.error ?? 'Failed'])));
    const ok = results.length - failed.length;
    if (failed.length) toast('Review', `${ok} done, ${failed.length} failed — see the highlighted cards`, 'error');
    else toast('Reviewed', `${ok} ${ok === 1 ? cfg.noun : cfg.nouns} — ${keep === 'afm' ? 'Weightrax now matches AFM' : 'AFM now matches Weightrax'}`, 'success');
    setSelected(new Set(failed.map((f) => f.id)));
    // The list page's status column changes too.
    void qc.invalidateQueries({ queryKey: [cfg.endpoint] });
    void refetch();
  };

  return (
    <div className="p-4 md:p-6 space-y-4">
      <div>
        <h1 className="text-xl font-semibold">{cfg.title}</h1>
        <nav className="mt-3 flex gap-1 border-b text-sm" aria-label={`${cfg.title} views`}>
          <Link className="px-3 py-2 text-muted-foreground hover:text-foreground" to={cfg.listPath}>Active</Link>
          <span className="px-3 py-2 border-b-2 border-primary font-medium" aria-current="page">Needs review{items.length ? ` (${items.length})` : ''}</span>
          <Link className="px-3 py-2 text-muted-foreground hover:text-foreground" to={`${cfg.listPath}?archived=true`}>Archived</Link>
          <Link className="px-3 py-2 text-muted-foreground hover:text-foreground" to={`${cfg.listPath}?includeArchived=1`}>All</Link>
        </nav>
      </div>

      <div className="rounded-md border bg-muted/30 p-3 text-sm space-y-1">
        <p>
          These {cfg.nouns} already existed in Weightrax (same code) when AFM first sent them, so AFM linked to the
          existing Weightrax {cfg.wtxNoun} instead of creating a duplicate. <strong>Until you choose which version to
          keep, AFM won’t send any changes for them to Weightrax.</strong>
        </p>
        <p className="text-muted-foreground">
          Tick one or more, then choose <strong>Keep AFM’s details</strong> (Weightrax is updated to match AFM) or{' '}
          <strong>Keep Weightrax’s details</strong> (AFM is updated to match Weightrax). You’ll see exactly what changes
          before anything is saved. Codes never change.
        </p>
      </div>

      {/* Action bar — sticky so the buttons stay in reach down a long list. */}
      <div className="sticky top-0 z-10 flex flex-wrap items-center gap-3 rounded-md border bg-background p-3">
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <Checkbox
            checked={allSelected}
            onCheckedChange={(v) => setSelected(v ? new Set(items.map((i) => i.id)) : new Set())}
            disabled={!items.length}
            aria-label="Select all"
          />
          Select all
        </label>
        <Button variant="ghost" size="sm" disabled={!items.some((i) => !i.differences.length)}
          onClick={() => setSelected(new Set(items.filter((i) => !i.differences.length).map((i) => i.id)))}>
          Select the ones that already match
        </Button>
        <span className="text-sm text-muted-foreground">{chosen.length} selected</span>
        <div className="flex-1" />
        <Button variant="secondary" disabled={!chosen.length || busy || unreadable.length > 0} onClick={() => setPending('weightrax')}
          title={unreadable.length ? 'Some selected records couldn’t be read from Weightrax' : undefined}>
          Keep Weightrax’s details{chosen.length ? ` (${chosen.length})` : ''}
        </Button>
        <Button disabled={!chosen.length || busy} onClick={() => setPending('afm')}>
          Keep AFM’s details{chosen.length ? ` (${chosen.length})` : ''}
        </Button>
      </div>

      {isLoading && <div className="text-sm text-muted-foreground">Reading AFM and Weightrax…</div>}
      {error && <div className="text-sm text-destructive">{(error as Error).message}</div>}
      {!isLoading && !error && items.length === 0 && (
        <div className="rounded-md border p-6 text-center text-sm text-muted-foreground">
          Nothing to review — every linked {cfg.noun} has been reviewed.
        </div>
      )}

      <div className={cn('space-y-3', isFetching && !isLoading && 'opacity-60')}>
        {items.map((item) => {
          const site = linkedSite(item);
          const w = site?.weightrax ?? null;
          const isSel = selected.has(item.id);
          return (
            <div
              key={item.id}
              role="button"
              tabIndex={0}
              onClick={() => toggle(item.id)}
              onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); toggle(item.id); } }}
              className={cn(
                'rounded-lg border-2 p-3 cursor-pointer transition-colors',
                isSel ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40',
                failures[item.id] && 'border-destructive',
              )}
            >
              <div className="flex items-center gap-3 mb-2">
                <Checkbox checked={isSel} onCheckedChange={() => toggle(item.id)} onClick={(e) => e.stopPropagation()} aria-label={`Select ${cfg.heading(item)}`} />
                <span className="font-semibold">{cfg.heading(item)}</span>
                {!w ? (
                  <span className="text-xs text-destructive">Couldn’t read the Weightrax {cfg.wtxNoun}</span>
                ) : item.differences.length ? (
                  <span className="text-xs rounded-full px-2 py-0.5 bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
                    Differs: {item.differences.map((d) => cfg.fields.find(([k]) => k === d)?.[1] ?? d).join(', ')}
                  </span>
                ) : (
                  <span className="text-xs rounded-full px-2 py-0.5 bg-green-100 text-green-800 dark:bg-green-950/40 dark:text-green-300">Details match</span>
                )}
                {w?.obsolete === 'Yes' && <span className="text-xs text-destructive">Obsolete in Weightrax</span>}
              </div>
              <div className="grid gap-2 md:grid-cols-2 text-sm">
                {([['AFM', item.afm], [`Weightrax${site ? ` (site ${site.weightraxSiteId}, id ${site.weightraxId ?? '?'})` : ''}`, w]] as const).map(([label, values]) => (
                  <div key={label} className="rounded-md border bg-background p-2">
                    <div className="text-xs font-medium text-muted-foreground mb-1">{label}</div>
                    {values ? (
                      <dl className="space-y-0.5">
                        {cfg.fields.map(([k, fl]) => (
                          <div key={k} className="flex gap-2">
                            <dt className="w-24 shrink-0 text-muted-foreground">{fl}</dt>
                            <dd className={cn(item.differences.includes(k) && 'font-medium text-amber-700 dark:text-amber-400')}>{show(values[k])}</dd>
                          </div>
                        ))}
                      </dl>
                    ) : <div className="text-destructive text-xs">Not found in Weightrax</div>}
                  </div>
                ))}
              </div>
              {failures[item.id] && <div className="mt-2 text-xs text-destructive">{failures[item.id]}</div>}
            </div>
          );
        })}
      </div>

      {pending && (
        <ConfirmDialog
          keep={pending}
          kind={kind}
          items={chosen}
          busy={busy}
          onCancel={() => setPending(null)}
          onConfirm={() => run(pending)}
        />
      )}
    </div>
  );
}

/** Spells out what the chosen action does, listing every value that will change. */
function ConfirmDialog({ keep, kind, items, busy, onCancel, onConfirm }: {
  keep: Keep; kind: WeightraxReviewKind; items: ReviewItem[]; busy: boolean; onCancel: () => void; onConfirm: () => void;
}) {
  const cfg = KINDS[kind];
  const n = items.length;
  const nouns = n === 1 ? cfg.noun : cfg.nouns;
  const changing = items.filter((i) => i.differences.length);
  const matching = n - changing.length;
  return (
    <Dialog open onOpenChange={(o) => { if (!o && !busy) onCancel(); }}>
      <DialogContent className="lg:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{keep === 'afm' ? 'Keep AFM’s details' : 'Keep Weightrax’s details'} for {n} {nouns}?</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          {keep === 'afm' ? (
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Weightrax is updated to match AFM.</strong> The weighbridge will show AFM’s details from now on.</li>
              <li>AFM is not changed.</li>
              <li>After this, changes made in AFM are sent to Weightrax again.</li>
            </ul>
          ) : (
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>AFM is updated to match Weightrax.</strong></li>
              <li>Weightrax is not changed — the weighbridge keeps showing what it shows today.</li>
              <li>After this, changes made in AFM are sent to Weightrax again.</li>
            </ul>
          )}
          {changing.length > 0 && (
            <div>
              <div className="font-medium mb-1">
                {keep === 'afm' ? 'Changes in Weightrax' : 'Changes in AFM'} ({changing.length}):
              </div>
              <ul className="max-h-60 overflow-y-auto rounded border divide-y text-xs">
                {changing.map((i) => {
                  const w = linkedSite(i)?.weightrax ?? {};
                  return (
                    <li key={i.id} className="px-2 py-1">
                      <span className="font-medium">{cfg.heading(i)}</span>
                      {i.differences.map((k) => {
                        const label = cfg.fields.find(([f]) => f === k)?.[1] ?? k;
                        const from = keep === 'afm' ? w[k] : i.afm[k];
                        const to = keep === 'afm' ? i.afm[k] : w[k];
                        return <div key={k} className="text-muted-foreground">{label}: “{show(from)}” → “{show(to)}”</div>;
                      })}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {matching > 0 && (
            <p className="text-muted-foreground">{matching} {matching === 1 ? 'already matches' : 'already match'} — nothing changes for {matching === 1 ? 'it' : 'them'} except being marked as reviewed.</p>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onCancel} disabled={busy}>Cancel</Button>
          <Button onClick={onConfirm} disabled={busy}>
            {busy ? 'Saving…' : `Confirm — ${keep === 'afm' ? 'update Weightrax' : 'update AFM'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
